import React from "react";
import AIHiringFullBundleCard from "./aiHiring/AIHiringFullBundleCard";
import Agent247BillingCard from "./agent247/Agent247BillingCard";
import BillingInfoCard from "./billing/BillingInfoCard";
import PaymentHistoryTable from "./billing/PaymentHistoryTable";
// Import AI Hiring CSS so all ai-* classes used by both plan selectors are available
import "./aiHiring/AiHiring.css";
import "./PaymentBilling.css";

/**
 * Payment & Billing page — thin wrapper that surfaces the consolidated AI
 * Hiring billing card and the (pilot-gated) Agent247 billing card as a
 * standalone, directly-navigable page from the sidebar.
 *
 * Each card owns its own Stripe checkout logic, status polling, and
 * success-URL handling, and renders nothing (returns null) while loading or
 * outside its own gate, so a section simply doesn't appear for a
 * not-yet-enabled agency rather than showing a disabled placeholder.
 *
 * Note: the old separate AIHiringPlanSelector (plain Member/Non-Member) and
 * AICallAssistPlanSelector (Platform Access + Data Usage bundle) components
 * are no longer rendered here — both are now consolidated into
 * AIHiringFullBundleCard. Neither old component file nor its backend routes
 * were deleted (both routes remain live for historical/in-flight sessions).
 * AIHiringPlanSelector is also no longer rendered on the AI Hiring settings
 * page (AiHiringSettings.js) as of the duplicate-checkout-removal pass —
 * both old components are now fully unreferenced dead code, left in place
 * rather than deleted — see MyAlbertAI_KB.md for the full rationale.
 */
const PaymentBilling = ({ activeLocation }) => {
  const locationId =
    String(activeLocation?.id || activeLocation?._id || "").trim();

  return (
    // "ai-hiring-page" defines the --ai-* CSS custom properties (--ai-primary,
    // --ai-border, --ai-text, etc.) that AiHiring.css's .ai-button-primary /
    // .ai-plan-item / .ai-card styles depend on. Every other page that renders
    // these shared components wraps in this class (see AiHiringSettings.js,
    // Agent247Settings.js, etc.) — PaymentBilling.js never did, so any
    // var(--ai-primary)-based *background* (a non-inherited property) silently
    // resolved to transparent while a hardcoded `color: #fff` stayed white,
    // producing invisible white-on-white button text. This was never noticed
    // before because the AI Hiring "Purchase" buttons are hidden behind the
    // active-plan success banner once a plan is purchased — the new Platform
    // Access button was the first ai-button-primary ever rendered on this page.
    <div className="pb-page ai-hiring-page">
      <div className="pb-header">
        <h1 className="pb-title">Payment &amp; Billing</h1>
        <p className="pb-subtitle">
          Manage your AI Hiring plan and AI Call Assist subscriptions
        </p>
      </div>

      <div className="pb-body">
        {locationId ? (
          <>
            <BillingInfoCard locationId={locationId} />

            {/* Consolidated AI Hiring billing: one combined card covering the
                admin-set Member/Non-Member plan tier + Platform Access +
                Data Usage, all via a single bundled Stripe Checkout Session.
                Replaces the old separate "AI Hiring" (Member/Non-Member) and
                "AI Hiring — Platform & Data" sections — see
                AIHiringFullBundleCard.jsx. Renders its own section title, or
                nothing at all if the agency hasn't been enabled for this
                section (Agency.aiCallAssistPaymentEnabled). */}
            <AIHiringFullBundleCard locationId={locationId} />

            {/* Agent247BillingCard is a separate Member/Non-Member billing
                flow (setup fee + call assist plan + platform fee) — also
                renders nothing outside its own pilot allowlist. */}
            <Agent247BillingCard locationId={locationId} />

            <PaymentHistoryTable locationId={locationId} />
          </>
        ) : (
          <p className="pb-no-location">
            No active location selected. Please select a location to view
            billing options.
          </p>
        )}
      </div>
    </div>
  );
};

export default PaymentBilling;
